import { ReactElement, ReactNode } from "react";
import { twMerge } from "tailwind-merge";
import { useDashboardContext } from "../dashboard/Dashboard.shared";
import { Icon, H5 } from "@sellar-io/components";
import { zLayer, FloatingLayerProvider } from "@sellar-io/components";
import { IconType } from "@sellar-io/components";
import { useMenuDrawerContext } from "../menu/MenuDrawerContextProvider.shared";
import { NewTag } from "../newTag/NewTag";
import { useFixedElementSpacer } from "../../hooks/useFixedElementSpacer";

export type NavigationBarProps = {
  leftIcon?: keyof typeof IconType;
  leftIconText?: string;
  title?: string;
  onClickLeftIcon?: () => void;
  onClickTitle?: () => void;
  position?: "sticky" | "fixed";
  borderBottom?: boolean;
  leftComponent?: ReactNode;
  rightComponent?: ReactNode;
  isNewComponent?: boolean;
  titleClassName?: string;
};

export const NavigationBarRoot = ({
  leftIcon,
  onClickLeftIcon,
  leftIconText,
  onClickTitle,
  title,
  position = "sticky",
  borderBottom = true,
  titleClassName,
  leftComponent,
  rightComponent,
  isNewComponent = false,
}: NavigationBarProps): ReactElement => {
  const { isMobileDisplayType, isMobileViewport } = useMenuDrawerContext();
  const backgroundClassName =
    import.meta.env.VITE_ENV === "production" ? "bg-white" : "bg-[#ffc6a1]";

  const leftIconAriaLabel =
    leftIconText ??
    (leftIcon === "MENU"
      ? "Open menu"
      : leftIcon === "CHEVRON_LEFT"
        ? "Go back"
        : "Navigation action");
  const isMobileViewportHeader = isMobileViewport && position === "sticky";

  return (
    <FloatingLayerProvider host="navigationBar">
      {isMobileViewportHeader && (
        <div
          aria-hidden="true"
          className="h-[var(--nav-height)] w-full shrink-0 print:hidden"
        />
      )}
      <header
        style={{
          backgroundColor:
            import.meta.env.VITE_ENV === "production" ? "white" : "#ffc6a1",
        }}
        className={twMerge(
          isMobileViewportHeader
            ? "fixed top-[calc(var(--refresh-pwa-banner-height)+var(--hidden-platform-banner-height))] right-0 left-0"
            : `${position} top-0`,
          `${zLayer.navigationBar} grid h-[var(--nav-height)] w-full shrink-0 grid-cols-[1fr_auto_1fr] items-center px-5 print:hidden`,
          borderBottom && "border-b border-solid border-gray-300",
          backgroundClassName,
        )}
      >
        <div className="flex min-w-0 justify-self-stretch">
          {leftIcon &&
          onClickLeftIcon &&
          (leftIcon !== "MENU" || isMobileDisplayType) ? (
            <button
              type="button"
              aria-label={leftIconAriaLabel}
              onClick={onClickLeftIcon}
              className="flex min-h-11 min-w-11 cursor-pointer flex-row items-center gap-1"
            >
              <Icon size={1} icon={leftIcon} />
              {leftIconText && (
                <span className="text-sm font-semibold whitespace-nowrap">
                  {leftIconText.toUpperCase()}
                </span>
              )}
            </button>
          ) : (
            (leftComponent ?? <div />)
          )}
        </div>
        {title ? (
          onClickTitle ? (
            <button
              type="button"
              onClick={onClickTitle}
              className="flex min-h-11 cursor-pointer flex-row items-center"
            >
              <H5 className={twMerge("whitespace-nowrap", titleClassName)}>
                {title}
              </H5>
              {isNewComponent && <NewTag className="ml-2" />}
            </button>
          ) : (
            <div className="flex flex-row items-center">
              <H5 className={twMerge("whitespace-nowrap", titleClassName)}>
                {title}
              </H5>
              {isNewComponent && <NewTag className="ml-2" />}
            </div>
          )
        ) : (
          <div />
        )}
        <div className="flex min-w-0 justify-end justify-self-stretch">
          {rightComponent ?? null}
        </div>
      </header>
    </FloatingLayerProvider>
  );
};

const Menu = (props: NavigationBarProps): ReactElement => {
  const { isMobileDisplayType, setIsOnScreen } = useMenuDrawerContext();
  const { isTwoPanel } = useDashboardContext();

  return (
    <NavigationBarRoot
      {...props}
      leftIcon={isMobileDisplayType ? "MENU" : undefined}
      onClickLeftIcon={
        isTwoPanel && !isMobileDisplayType
          ? undefined
          : () => setIsOnScreen(true)
      }
    />
  );
};

const SubHeader = ({
  children,
  className,
  borderBottom = true,
}: {
  children: ReactNode;
  className?: string;
  borderBottom?: boolean;
}) => {
  const { isMobileViewport } = useMenuDrawerContext();
  const { contentRef, spacerRef } =
    useFixedElementSpacer<HTMLDivElement>(isMobileViewport);

  return (
    <FloatingLayerProvider host="navigationBar">
      {isMobileViewport && (
        <div
          ref={spacerRef}
          aria-hidden="true"
          className="w-full shrink-0 print:hidden"
        />
      )}
      <div
        ref={contentRef}
        className={twMerge(
          `isolate ${zLayer.navigationSubBar}`,
          isMobileViewport
            ? "fixed top-[calc(var(--refresh-pwa-banner-height)+var(--hidden-platform-banner-height)+var(--nav-height))] right-0 left-0"
            : "sticky top-[var(--nav-height)]",
          borderBottom && "border-b border-solid",
          import.meta.env.VITE_ENV === "production"
            ? "bg-white"
            : "bg-[#ffc6a1]",
          className,
        )}
      >
        {children}
      </div>
    </FloatingLayerProvider>
  );
};

export {
  NavigationBarRoot as NavigationBar,
  Menu as NavigationBarMenu,
  SubHeader as NavigationBarSubHeader,
};
